import { CompanyInventoryItem, inventoryKindLabel } from '../../types/companyInventory';
import { FinalInspectionAttachment, FinalInspectionItem } from '../../types/finalInspection';
import InspectionRemarkFiles from './InspectionRemarkFiles';
import './InspectionFormSheet.css';
import './ProductionDailyReportPanel.css';

export interface FinalSheetValues {
  inventoryId: number | null;
  itemKind: string;
  itemName: string;
  specText: string;
  authorName: string;
  reviewerName?: string;
  approverName?: string;
  lotNo: string;
  lotQty: string;
  inspectorName: string;
  inspectionDate: string;
  overallJudgment: string;
  remark: string;
  items: FinalInspectionItem[];
}

interface Props {
  values: FinalSheetValues;
  editable?: boolean;
  attachmentsEditable?: boolean;
  inventoryOptions?: CompanyInventoryItem[];
  remarkAttachments?: FinalInspectionAttachment[];
  pendingRemarkFiles?: File[];
  onChange?: (patch: Partial<FinalSheetValues>) => void;
  onItemChange?: (index: number, patch: Partial<FinalInspectionItem>) => void;
  onAddItem?: () => void;
  onRemoveItem?: (index: number) => void;
  onOpenModelPicker?: () => void;
  onOpenMidLotPicker?: () => void;
  lotNoHint?: string;
  onPickRemarkFiles?: (files: File[]) => void;
  onRemovePendingRemarkFile?: (index: number) => void;
  onDownloadAttachment?: (file: FinalInspectionAttachment) => void;
  onDeleteAttachment?: (file: FinalInspectionAttachment) => void;
}

function modelPickSummary(itemName: string, specText?: string | null, itemKind?: string) {
  const name = itemName?.trim();
  if (!name) return '';
  const kind = itemKind ? `[${inventoryKindLabel(itemKind)}] ` : '';
  return `${kind}${name}${specText?.trim() ? ` · ${specText.trim()}` : ''}`;
}

function FinalInspectionSheet({
  values,
  editable = false,
  attachmentsEditable = editable,
  remarkAttachments = [],
  pendingRemarkFiles = [],
  onChange,
  onItemChange,
  onAddItem,
  onRemoveItem,
  onOpenModelPicker,
  onOpenMidLotPicker,
  onPickRemarkFiles,
  onRemovePendingRemarkFile,
  onDownloadAttachment,
  onDeleteAttachment,
  lotNoHint,
}: Props) {
  const field = (key: keyof FinalSheetValues, value: string) =>
    onChange?.({ [key]: value } as Partial<FinalSheetValues>);

  const modelSummary = modelPickSummary(values.itemName, values.specText, values.itemKind);

  return (
    <div className="ifs-sheet">
      <div className="ifs-sheet__top">
        <div>
          <h4 className="ifs-sheet__title">제품검사성적서</h4>
        </div>
        <div className="ifs-sheet__stamps">
          {[
            ['작성', values.authorName],
            ['검토', values.reviewerName],
            ['승인', values.approverName],
          ].map(([label, value]) => (
            <div key={label}>
              <strong>{label}</strong>
              <span
                style={{
                  color: value ? '#1f2a37' : '#98a2b3',
                  fontWeight: value ? 600 : 400,
                }}
              >
                {value || '-'}
              </span>
            </div>
          ))}
        </div>
      </div>

      <section className="ifs-sheet__field-group">
        <h5 className="ifs-sheet__field-group-title">검사 대상</h5>
        <div className="ifs-sheet__fields is-cols-4">
          <label className="ifs-sheet__item-pick">
            모델명 (재고관리)
            {editable ? (
              <div className="ifs-sheet__item-row">
                <button
                  type="button"
                  className={`pdr-pick-btn${modelSummary ? ' has-value' : ''}`}
                  onClick={() => onOpenModelPicker?.()}
                >
                  {modelSummary || '모델 선택'}
                </button>
              </div>
            ) : (
              <input
                value={
                  values.itemName
                    ? `${values.itemName}${values.specText ? ` · ${values.specText}` : ''}`
                    : '-'
                }
                readOnly
              />
            )}
          </label>
          <label>
            생산 LOT NO.
            {editable ? (
              <div className="ifs-sheet__item-row">
                <input
                  value={values.lotNo}
                  readOnly
                  placeholder={
                    values.lotNo
                      ? undefined
                      : onOpenMidLotPicker
                        ? '저장 시 자동 발급 · 또는 Lot 선택'
                        : '저장 시 자동 발급'
                  }
                />
                {onOpenMidLotPicker ? (
                  <button
                    type="button"
                    className="is-ghost"
                    title={
                      values.inventoryId
                        ? '승인된 중간검사 생산 Lot에서 선택'
                        : '모델을 먼저 선택하세요'
                    }
                    disabled={!values.inventoryId}
                    onClick={onOpenMidLotPicker}
                  >
                    Lot 선택
                  </button>
                ) : null}
              </div>
            ) : (
              <input value={values.lotNo || '-'} readOnly />
            )}
            {editable && lotNoHint ? <span className="ifs-field-hint">{lotNoHint}</span> : null}
          </label>
          <label>
            생산 LOT 수량
            <input
              value={values.lotQty}
              readOnly={!editable}
              onChange={(e) => field('lotQty', e.target.value)}
              placeholder="중간검사 선택 시 자동 반영"
            />
          </label>
          <label>
            검사 일자
            <input
              type="date"
              value={values.inspectionDate}
              readOnly={!editable}
              onChange={(e) => field('inspectionDate', e.target.value)}
            />
          </label>
          <label>
            검사자
            <input
              value={values.inspectorName}
              readOnly={!editable}
              onChange={(e) => field('inspectorName', e.target.value)}
            />
          </label>
        </div>
      </section>

      <section className="ifs-sheet__field-group ifs-req">
        <div className="ifs-req__head">
          <div>
            <h5 className="ifs-sheet__field-group-title" style={{ margin: 0 }}>
              검사 항목
            </h5>
            <p className="ifs-req__hint">
              {values.items.length > 0
                ? `${values.items.length}개 항목`
                : editable
                  ? '검사항목을 추가해 주세요'
                  : '등록된 항목 없음'}
            </p>
          </div>
          {editable ? (
            <button type="button" className="ifs-req__add" onClick={() => onAddItem?.()}>
              + 행 추가
            </button>
          ) : null}
        </div>

        {values.items.length === 0 ? (
          <div className="ifs-req__empty">
            <strong>검사 항목이 없습니다</strong>
            <span>{editable ? '행 추가로 검사항목·기준·방법을 입력하세요.' : ''}</span>
          </div>
        ) : (
          <div className="ifs-req__list">
            {values.items.map((item, index) => {
              const judgment = (item.judgment ?? '').trim();
              const judgmentClass =
                judgment === '적합'
                  ? 'is-pass'
                  : judgment === '부적합'
                    ? 'is-fail'
                    : 'is-pending';
              return (
                <article key={item.itemId ?? `final-item-${index}`} className="ifs-req__card">
                  <header className="ifs-req__card-top">
                    <span className="ifs-req__no">{index + 1}</span>
                    <div className="ifs-req__title-wrap">
                      {editable ? (
                        <textarea
                          className="ifs-req__title-input"
                          value={item.itemName ?? ''}
                          onChange={(e) => onItemChange?.(index, { itemName: e.target.value })}
                          placeholder="검사항목명"
                          rows={1}
                        />
                      ) : (
                        <strong className="ifs-req__title-text">{item.itemName || '-'}</strong>
                      )}
                    </div>
                    <div className={`ifs-req__judgment ${judgmentClass}`}>
                      {editable ? (
                        <select
                          value={item.judgment ?? ''}
                          onChange={(e) => onItemChange?.(index, { judgment: e.target.value })}
                        >
                          <option value="">판정</option>
                          <option value="적합">적합</option>
                          <option value="부적합">부적합</option>
                        </select>
                      ) : (
                        <span>{judgment || '-'}</span>
                      )}
                    </div>
                    {editable ? (
                      <button
                        type="button"
                        className="ifs-req__remove"
                        onClick={() => onRemoveItem?.(index)}
                        title="삭제"
                        aria-label="검사항목 삭제"
                      >
                        삭제
                      </button>
                    ) : null}
                  </header>

                  <div className="ifs-req__body">
                    <label className="ifs-req__field">
                      <span>검사기준</span>
                      {editable ? (
                        <textarea
                          value={item.standardText ?? ''}
                          onChange={(e) =>
                            onItemChange?.(index, { standardText: e.target.value })
                          }
                          placeholder="합격 기준을 입력하세요"
                          rows={3}
                        />
                      ) : (
                        <p className="ifs-req__readonly">{item.standardText || '-'}</p>
                      )}
                    </label>
                    <label className="ifs-req__field">
                      <span>검사방법 및 조건</span>
                      {editable ? (
                        <textarea
                          value={item.methodText ?? ''}
                          onChange={(e) => onItemChange?.(index, { methodText: e.target.value })}
                          placeholder="검사 방법·조건을 입력하세요"
                          rows={3}
                        />
                      ) : (
                        <p className="ifs-req__readonly">{item.methodText || '-'}</p>
                      )}
                    </label>
                  </div>

                  <div className="ifs-req__samples">
                    <span className="ifs-req__samples-label">시료</span>
                    <div className="ifs-req__sample-grid">
                      {(['sample1', 'sample2', 'sample3', 'sample4', 'sample5'] as const).map(
                        (key, sampleIndex) => (
                          <label key={key} className="ifs-req__sample">
                            <span>X{sampleIndex + 1}</span>
                            <input
                              value={item[key] ?? ''}
                              readOnly={!editable}
                              onChange={(e) =>
                                onItemChange?.(index, { [key]: e.target.value })
                              }
                              placeholder="-"
                            />
                          </label>
                        )
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <div
        className={`ifs-overall${
          values.overallJudgment === '적합'
            ? ' is-pass'
            : values.overallJudgment === '부적합'
              ? ' is-fail'
              : ''
        }`}
      >
        <div className="ifs-overall__head">
          <div>
            <h5 className="ifs-sheet__field-group-title" style={{ margin: 0 }}>
              종합판정
              {editable ? <span className="ifs-field-required" aria-hidden> *</span> : null}
            </h5>
            <p className="ifs-overall__hint">
              검사 항목을 모두 확인한 뒤 최종 판정하세요
            </p>
          </div>
          {!editable && values.overallJudgment ? (
            <span
              className={`ifs-overall__badge${
                values.overallJudgment === '적합'
                  ? ' is-pass'
                  : values.overallJudgment === '부적합'
                    ? ' is-fail'
                    : ''
              }`}
            >
              {values.overallJudgment}
            </span>
          ) : null}
        </div>
        {editable ? (
          <div className="ifs-overall__choices" role="group" aria-label="종합판정">
            <button
              type="button"
              className={`ifs-overall__choice is-pass${
                values.overallJudgment === '적합' ? ' is-selected' : ''
              }`}
              onClick={() => field('overallJudgment', '적합')}
            >
              <strong>적합</strong>
              <span>합격</span>
            </button>
            <button
              type="button"
              className={`ifs-overall__choice is-fail${
                values.overallJudgment === '부적합' ? ' is-selected' : ''
              }`}
              onClick={() => field('overallJudgment', '부적합')}
            >
              <strong>부적합</strong>
              <span>불합격 · NCR 검토</span>
            </button>
          </div>
        ) : !values.overallJudgment ? (
          <p className="ifs-overall__empty">판정 없음</p>
        ) : null}
      </div>

      <div className="ifs-sheet__remark">
        <div className="ifs-sheet__toolbar">
          <strong>비고</strong>
          {attachmentsEditable ? (
            <label className="ifs-sheet__file-btn is-soft">
              파일 첨부
              <input
                type="file"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  if (files.length) onPickRemarkFiles?.(files);
                  e.target.value = '';
                }}
              />
            </label>
          ) : null}
        </div>
        {editable ? (
          <textarea
            value={values.remark}
            onChange={(e) => field('remark', e.target.value)}
            rows={3}
            placeholder="공인기관 성적서 대체 시 비고와 함께 첨부하세요."
            style={{ marginBottom: 8 }}
          />
        ) : (
          <p style={{ margin: '0 0 8px', fontSize: 13, whiteSpace: 'pre-wrap' }}>
            {values.remark || '-'}
          </p>
        )}
        <InspectionRemarkFiles
          attachments={remarkAttachments}
          pendingFiles={pendingRemarkFiles}
          editable={attachmentsEditable}
          emptyText="공인기관 성적서 등 비고 첨부파일이 없습니다."
          onDownload={onDownloadAttachment}
          onDeleteSaved={onDeleteAttachment}
          onRemovePending={onRemovePendingRemarkFile}
        />
      </div>
    </div>
  );
}

export default FinalInspectionSheet;
